import { SimEventKind } from "@highmarch/domain";
import type { SimEvent } from "@highmarch/domain";
import { roundOf, shotKey } from "../render/statusFx";

/** A kill whose round is still in the air: what it paid, and the ImpactGate key and enemy that land it. */
export interface KillLanding { readonly key: number; readonly enemy: number; readonly points: number }

/**
 * The kills among `events[from…]` that show only when their round lands (statusFx.ts ImpactGate): those brought by a
 * tower's round of this tick, when rounds fly at all (`gated`: the sprite VFX is up). The enemy view holds the death
 * until then; the HUD's points and the moments wait on the same landings (TOWER_FX / GUIDE follow-ups), so the pill
 * never counts a kill the player has not seen yet.
 */
export function killLandings(events: readonly SimEvent[], from: number, tick: number, gated: boolean): KillLanding[] {
  if (!gated || from >= events.length) return [];
  const rounds = roundOf(events), out: KillLanding[] = [];
  for (let i = from; i < events.length; i++) {
    const ev = events[i];
    if (ev.kind === SimEventKind.EnemyKilled && rounds[i] > 0) out.push({ key: shotKey(tick, rounds[i]), enemy: ev.a, points: ev.c });
  }
  return out;
}

/** Runs `fn` for a landing (ImpactGate.wait in the level; a fake in tests). */
export type LandingWait = (key: number, enemy: number, fn: () => void) => void;

/**
 * The points the HUD holds back: kill rewards whose killing round has not landed yet. The pill shows the sim's points
 * less these, so it rolls up as the round hits, not as the tower fires.
 */
export class PendingRewards {
  private owed = 0;
  get pending(): number { return this.owed; }

  observe(landings: readonly KillLanding[], wait: LandingWait): void {
    for (const l of landings) {
      if (l.points <= 0) continue;
      this.owed += l.points;
      wait(l.key, l.enemy, () => { this.owed = Math.max(0, this.owed - l.points); });
    }
  }

  /** The points to show for the sim's `points`. */
  shown(points: number): number { return Math.max(0, points - this.owed); }
}

/** Runs `fn` once every landing in `landings` has landed (at once when there are none). */
export function afterLandings(landings: readonly KillLanding[], wait: LandingWait, fn: () => void): void {
  let left = landings.length;
  if (left === 0) { fn(); return; }
  for (const l of landings) wait(l.key, l.enemy, () => { if (--left === 0) fn(); });
}
